import type { ReactNode } from "react";

/** Stroke icons, drawn on a 24px grid. Decorative: every use sits beside a label or an aria-label. */
const PATHS = {
  credits: (
    <>
      <path d="M4 8a2 2 0 002-2h12a2 2 0 002 2v2a2 2 0 000 4v2a2 2 0 00-2 2H6a2 2 0 00-2-2v-2a2 2 0 000-4z" />
      <path d="M13.5 6v12" strokeDasharray="2 2" />
    </>
  ),
  points: <path d="M12 3.8l2.5 5.1 5.6.8-4 3.9.9 5.6-5-2.6-5 2.6.9-5.6-4-3.9 5.6-.8z" />,
  bilt: <path d="M4 11l8-6 8 6v8a1 1 0 01-1 1h-4.5v-5h-5v5H5a1 1 0 01-1-1z" />,
  cards: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M3 10h18M7 15h3" />
    </>
  ),
  offers: (
    <>
      <path d="M3.5 11.5V4.5a1 1 0 011-1h7l9 9-8 8z" />
      <circle cx="8" cy="8" r="1.3" />
    </>
  ),
  flights: (
    <path
      d="M12 2.5c-.8 0-1.4.7-1.4 1.5v5.3L4 13v2.2l6.6-1.8v4.3L8.3 19v1.5l3.7-.9 3.7.9V19l-2.3-1.3v-4.3l6.6 1.8V13l-6.6-3.7V4c0-.8-.6-1.5-1.4-1.5Z"
      fill="currentColor"
      stroke="none"
    />
  ),
  travel: (
    <>
      <rect x="5" y="7" width="14" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M9 11v5M15 11v5M8 22v-2M16 22v-2" />
    </>
  ),
  hotel: (
    <>
      <path d="M4 20V5a1 1 0 011-1h9a1 1 0 011 1v15M15 10h4a1 1 0 011 1v9M2 20h20" />
      <path d="M7 8h1M11 8h1M7 12h1M11 12h1M7 16h1M11 16h1M18 14h.01" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
    </>
  ),
  /**
   * The capture control. Deliberately NOT a circular reload arrow: that glyph
   * means "reload what I am looking at", which is the one thing a run does not
   * do. It opens the issuers' own sites in other tabs and lands in Points,
   * Offers and Bilt. A window with an inbound arrow says "pull from a site".
   */
  capture: (
    <>
      <rect x="3" y="4.5" width="18" height="12" rx="2" />
      <path d="M3 8.5h18M12 10v4.5M9.8 12.3L12 14.5l2.2-2.2M8 20h8" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  pin: <path d="M15 3l6 6-3 1-4 4 1 5-2 2-4-4-5 5-1-1 5-5-4-4 2-2 5 1 4-4z" />,
  chevron: <path d="M9 6l6 6-6 6" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof PATHS;

export function Icon({
  name,
  size = 20,
  strokeWidth = 1.8,
  className,
}: {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
}) {
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {PATHS[name]}
    </svg>
  );
}
